import { useCallback, useEffect, useRef, type MouseEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { type ListChildComponentProps, FixedSizeList } from 'react-window'
import _sum from 'lodash/sum'
import cn from 'clsx'

import GroupInfo from './GroupInfo'
import PolygonListItem from '../PolygonListItem/PolygonListItem'
import useClassName from '../../hooks/use_class_name'
import { type Emissions, type EmissionsGroup } from 'types/data'

import { CLASS_NAME } from './const'

import './style.scss'

type Props = {
  className?: string
  group: EmissionsGroup
  selected?: boolean
  onClick?: () => void
  width: number
  selectedEmissions: Emissions[]
  setSelectedEmissions: (emissions: Emissions[]) => void
  isExpanded: boolean
  onToggleExpand: (event: MouseEvent) => void
  groupScrollOffset: number
  setGroupScrollOffset: (offset: number) => void
}

export default function GroupListItem({
  setSelectedEmissions,
  selectedEmissions,
  className,
  selected,
  width,
  group,
  onClick,
  isExpanded,
  onToggleExpand,
  groupScrollOffset,
  setGroupScrollOffset
}: Props) {
  const { t } = useTranslation()
  const finalClassName = useClassName(CLASS_NAME, className)
  const { polygons, key } = group
  const listRef = useRef<FixedSizeList>(null)
  const groupTotalArea = _sum(
    polygons.map(
      ({ feature }: { feature: any }) => feature?.properties?.area ?? 0
    )
  )

  useEffect(() => {
    if (
      isExpanded &&
      Number.isInteger(groupScrollOffset) &&
      groupScrollOffset > 0 &&
      listRef.current != null
    ) {
      listRef.current.scrollTo(groupScrollOffset)
    }
  }, [isExpanded, groupScrollOffset])

  const allSelectedPolygonIds = selectedEmissions.map(({ id }) => id)
  const groupPolygonIds = group.polygons.map(({ id }) => id)
  const selectedPolygonIdsFromGroup = groupPolygonIds.filter((id) =>
    allSelectedPolygonIds.includes(id)
  )

  const groupTotalNEE = _sum(
    polygons.map(({ total }: { total: number }) => total)
  )

  const handlePolygonCardClick = useCallback(
    (emissions: Emissions, e: MouseEvent): boolean => {
      e.preventDefault()
      e.stopPropagation()

      const { id: eID } = emissions
      const isFeatureSelected =
        selectedEmissions.find(({ id }) => id === eID) != null

      if (isFeatureSelected) {
        const newSelectedEmissions = selectedEmissions.filter(
          ({ id }) => id !== eID
        )
        setSelectedEmissions(newSelectedEmissions)
      } else {
        setSelectedEmissions([...selectedEmissions, emissions])
      }

      return false
    },
    [selectedEmissions, setSelectedEmissions]
  )

  const FeatureListRow = ({ index, style, data }: ListChildComponentProps) => {
    const emissions: Emissions = data[index]
    const polygonId = emissions.id
    const isSelected = allSelectedPolygonIds.includes(polygonId)
    const indexOfSelectedPolygon = allSelectedPolygonIds.findIndex(
      (id) => id === polygonId
    )

    const finalStyle = {
      ...style,
      top: Number(style?.top ?? 0) + 16,
      left: Number(style?.left ?? 0) + 32,
      width: Math.max(0, +(width ?? 0) - 48)
    }

    return (
      <li
        style={finalStyle}
        onClick={handlePolygonCardClick.bind(null, emissions)}
      >
        <PolygonListItem
          emissions={emissions}
          selected={isSelected}
          showSelectionIndex={isSelected}
          selectionIndex={indexOfSelectedPolygon}
        />
      </li>
    )
  }

  return (
    <div
      onClick={onClick}
      className={cn(finalClassName, {
        selected,
        expanded: isExpanded
      })}
    >
      <div className={`${CLASS_NAME}-header`}>
        <h3>{key}</h3>
        <p>
          {polygons.length.toLocaleString()}{' '}
          {t('group_list_item.assets_suffix')}
        </p>
        <p
          className={cn(`${CLASS_NAME}-toggle-expansion`, {
            selected: isExpanded
          })}
          onClick={(event) => {
            event.preventDefault()
            event.stopPropagation()

            onToggleExpand(event)
          }}
        >
          {isExpanded
            ? t('group_list_item.collapse')
            : t('group_list_item.expand')}
        </p>
      </div>
      {isExpanded ? (
        <ul className={`${CLASS_NAME}-list`}>
          <FixedSizeList
            ref={listRef}
            height={150}
            itemSize={80}
            width={width}
            itemData={polygons}
            itemCount={polygons.length}
            onScroll={({ scrollOffset }) => {
              setGroupScrollOffset?.(scrollOffset)
            }}
          >
            {FeatureListRow}
          </FixedSizeList>
        </ul>
      ) : (
        <GroupInfo
          groupTotalNEE={groupTotalNEE}
          groupTotalArea={groupTotalArea}
        />
      )}
      {selectedPolygonIdsFromGroup.length > 0 && (
        <div className={`${CLASS_NAME}-selection-badge`}>
          <p>{selectedPolygonIdsFromGroup.length}</p>
        </div>
      )}
    </div>
  )
}

export { CLASS_NAME }
